import React, { useState, useRef, useEffect } from "react";
import { uploadImagesToS3, getSignedUrlFromS3, deleteFileFromS3 } from "~/utils/s3Utils";
import { getCustomerId } from "~/components/db/CharacterDBUtils";
import "~/css/FloorMap.css";
import "~/css/Button.css";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import * as icon from "~/Icons";
import { PlaySound } from "~/SoundUtils";
import { volumeAtom } from "~/Atom";
import { floorImagesAtom } from "~/AtomWithDynamoDB";
import { FloorImageType } from "~/Commons";
import SwipeSlider from "~/components/utils/SwipeSlider";
import ConfirmationModal from "~/components/modal/ConfirmationModal";
import { useIsAwsSettingPage, useIsAwsChatPage } from "~/utils/envUtils";

/**
 * フロアマップ設定コンポーネント(アコーディオン用)
 */
// eslint-disable-next-line complexity
const FloorMapSettings: React.FC = () => {
  const { t } = useTranslation();
  const [currentFloor, setCurrentFloor] = useState(1);
  const [floorImages, setFloorImages] = useAtom(floorImagesAtom);
  const [floorImagesForView, setFloorImagesForView] = useState<FloorImageType[]>([]);
  const [direction, setDirection] = useState<"next" | "prev" | null>(null);
  const [volume] = useAtom(volumeAtom);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [registeredImageCount, setRegisteredImageCount] = useState(0);

  const isAwsChatPage = useIsAwsChatPage();
  const isAwsSettingPage = useIsAwsSettingPage();

  const [isLocalStorageRef, setIsLocalStorageRef] = useState(false);

  // floorImagesが変化したら署名付きURLを取得してfloorImagesForViewを更新
  useEffect(() => {
    if (process.env.REACT_APP_SYSTEM_REQUIREMENT !== "aws") return;

    // AWS環境でチャット画面の場合、ローカルストレージを参照しているか確認
    if (isAwsChatPage) {
      const localStoredFloorImages = localStorage.getItem("floorImages");
      if (localStoredFloorImages) {
        try {
          const parsedImages: FloorImageType[] = JSON.parse(localStoredFloorImages);
          if (parsedImages.length > 0) {
            setIsLocalStorageRef(true);
          }
        } catch (error) {
          console.error("ローカルストレージのフロアマップデータの解析に失敗しました:", error);
        }
      }
    }

    if (!floorImages || floorImages.length === 0) {
      setFloorImagesForView([]);
      return;
    }

    (async () => {
      try {
        const signedUrls = await Promise.all(
          floorImages.map((img: FloorImageType) => getSignedUrlFromS3(img.src))
        );
        setFloorImagesForView(
          floorImages.map((img: FloorImageType, idx: number) => ({
            src: signedUrls[idx] || img.src,
            name: img.name,
          }))
        );
      } catch (error) {
        console.error("署名付きURLの取得に失敗しました:", error);
      }
    })();
  }, [floorImages, isAwsChatPage]);

  /**
   * 階層を切り替える関数(次の階へ)
   */
  const handleNext = () => {
    if (floorImages && currentFloor < floorImages.length) {
      setDirection("next");
      setCurrentFloor((prev) => prev + 1);
      PlaySound("push", volume);
    }
  };

  /**
   * 階層を切り替える関数(前の階へ)
   */
  const handlePrev = () => {
    if (currentFloor > 1) {
      setDirection("prev");
      setCurrentFloor((prev) => prev - 1);
      PlaySound("push", volume);
    }
  };

  /**
   * S3キーを抽出する関数
   * @param images フロア画像の配列
   * @returns S3キーの配列
   */
  const extractS3Keys = (images: FloorImageType[]) => {
    return images
      .map((img) => {
        if (!img.src) return null;
        if (img.src.startsWith("http") || img.src.startsWith("data:")) {
          const match = img.src.match(/([\w-]+\/floorImages\/[^?]+)/);
          return match ? match[1] : null;
        }
        return img.src;
      })
      .filter(Boolean) as string[];
  };

  /**
   * S3から画像を削除する関数
   * @param keys S3キーの配列
   * @return 削除処理のPromise
   */
  const deleteImagesFromS3 = async (keys: string[]) => {
    for (const key of keys) {
      await deleteFileFromS3(key);
    }
  };

  /**
   * フロアマップの画像を初期化する関数
   * @return 初期化処理のPromise
   */
  const initFloorImages = async () => {
    const prevFloorImages = [...floorImages];
    const localStoredFloorImages = localStorage.getItem("floorImages");

    setFloorImages([]);
    setCurrentFloor(1);

    if ((isAwsSettingPage || (isAwsChatPage && isLocalStorageRef)) && prevFloorImages.length > 0) {
      if (!isLocalStorageRef) {
        const oldKeys = extractS3Keys(prevFloorImages);
        await deleteImagesFromS3(oldKeys);
      } else if (isAwsChatPage && isLocalStorageRef) {
        try {
          const parsedImages: FloorImageType[] = JSON.parse(localStoredFloorImages as string);
          const oldKeys = extractS3Keys(parsedImages);
          await deleteImagesFromS3(oldKeys);
        } catch (error) {
          console.error("ローカルストレージのフロアマップデータの解析に失敗しました:", error);
        }
        localStorage.removeItem("floorImages");
        setIsLocalStorageRef(false);
      }
    }
  };

  /**
   * 登録ボタン押下時
   * @return void
   */
  const handleRegisterClick = () => {
    fileInputRef.current?.click();
    PlaySound("push", volume);
  };

  /**
   * 初期化ボタン押下時
   * @return void
   */
  const handleInitClick = () => {
    if (!window.confirm(t("initMessage"))) return;

    initFloorImages();
    PlaySound("push", volume);
    if (isAwsChatPage) {
      alert(t("reloadForInit"));
      window.location.reload();
    }
  };

  /**
   * フロアマップ更新
   * @param e ファイル選択イベント
   * @return void
   */
  // eslint-disable-next-line complexity
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const sortedFiles = Array.from(files).sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
    );

    try {
      let images: FloorImageType[] = [];
      if (process.env.REACT_APP_SYSTEM_REQUIREMENT === "aws") {
        const customerId = await getCustomerId();
        const s3Keys = await uploadImagesToS3(sortedFiles, "floorImages", customerId);
        images = s3Keys.map((key, idx) => ({
          src: key,
          name: sortedFiles[idx].name.replace(/\.[^/.]+$/, ""),
        }));
      } else {
        images = await Promise.all(
          sortedFiles.map((file) => {
            return new Promise<FloorImageType>((resolve) => {
              const reader = new FileReader();
              reader.onload = () => {
                const replaceFileName = file.name.replace(/\.[^/.]+$/, "");
                resolve({ src: reader.result as string, name: replaceFileName });
              };
              reader.readAsDataURL(file);
            });
          })
        );
      }

      if (images.length > 0) {
        const prevFloorImages = [...floorImages];
        setFloorImages(images);

        if (process.env.REACT_APP_SYSTEM_REQUIREMENT === "aws" && prevFloorImages.length > 0) {
          // Web管理画面またはチャット画面でローカルストレージ参照時は古い画像を削除
          if (!isAwsChatPage || isLocalStorageRef) {
            const oldKeys = extractS3Keys(prevFloorImages);
            await deleteImagesFromS3(oldKeys);
          }
        }
        setCurrentFloor(1);

        setRegisteredImageCount(images.length);
        setIsSuccessModalOpen(true);

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    } catch (error) {
      console.error("ファイルの読み込みまたはアップロードに失敗しました:", error);
    }
  };

  /**
   * 成功モーダルを閉じる
   * @return void
   */
  const handleSuccessModalClose = () => {
    setIsSuccessModalOpen(false);
    PlaySound("push", volume);
  };

  const activeFloorImages =
    process.env.REACT_APP_SYSTEM_REQUIREMENT === "aws" ? floorImagesForView : floorImages;

  return (
    <div className="floor-map-settings">
      <div className="floor-map-content">
        <div className="image-name">
          {(activeFloorImages?.length ?? 0) > 0 && activeFloorImages[currentFloor - 1]?.name}
        </div>
        <div className={`image-container ${direction}`}>
          {(activeFloorImages?.length ?? 0) > 0 ? (
            <SwipeSlider
              currentIndex={currentFloor - 1}
              onIndexChange={(newIndex) => setCurrentFloor(newIndex + 1)}
              className="swipe-slider-container"
            >
              {activeFloorImages?.map((items, idx) => (
                <img key={idx} src={items.src} alt={items.name} />
              ))}
            </SwipeSlider>
          ) : (
            <h3>{t("noFloorMap")}</h3>
          )}

          <input
            type="file"
            accept="image/*"
            multiple
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
          />

          <button
            className="btn btn-circle floor-nav-button prev"
            onClick={handlePrev}
            disabled={currentFloor === 1 || (floorImages?.length ?? 0) <= 1}
            aria-label={t("previousFloor")}
          >
            <icon.icons_arrow_left />
          </button>

          <button
            className="btn btn-circle floor-nav-button next"
            onClick={handleNext}
            disabled={
              currentFloor === (floorImages?.length ?? 0) || (floorImages?.length ?? 0) <= 1
            }
            aria-label={t("nextFloor")}
          >
            <icon.icons_arrow_right />
          </button>
        </div>
      </div>

      <div className="file-input-container">
        <button
          className={`button-0 flexible ${!isLocalStorageRef && isAwsChatPage ? "disabled-btn" : ""}`}
          onClick={handleInitClick}
          disabled={!isLocalStorageRef && isAwsChatPage}
        >
          {t("reset")}
        </button>
        <button className="button-1 flexible" onClick={handleRegisterClick}>
          {(floorImages?.length ?? 0) > 0 ? t("update") : t("register")}
        </button>
      </div>

      <ConfirmationModal
        show={isSuccessModalOpen}
        handleClose={handleSuccessModalClose}
        handleConfirm={handleSuccessModalClose}
        title={t("registerSuccess")}
        body={t("registerSuccessMessage", { count: registeredImageCount })}
        cancelText=""
        confirmText="OK"
        variant="success"
      />
    </div>
  );
};

export default FloorMapSettings;
